import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { CapsuleGroupCapsule } from '@/tools/manualCondition/manualCondition.constants';

export interface CapsuleGroupState {
  capsules: CapsuleGroupCapsule[];
  isLoading: boolean;
}

export interface CapsuleGroupActions {
  /** Sets whether the capsule group is waiting on capsules to be fetched. */
  setLoading: (isLoading: boolean) => void;
  /** Replaces the capsules in the group, narrowing each entry down to its persisted fields, and clears isLoading. */
  setCapsules: (capsules: CapsuleGroupCapsule[]) => void;
  /** Appends a single capsule to the group, narrowing it down to its persisted fields. */
  addCapsule: (capsule: CapsuleGroupCapsule) => void;
  /** Resets the store to its initial state, applying any supplied overrides on top of it. */
  reset: ResettableStoreState<CapsuleGroupState>;
}

/**
 * Rebuilds the capsule as a new object containing only its persisted fields. This guarantees every capsule in the
 * store is a distinct object reference, even when the caller passes the same source object into multiple calls
 * (e.g. clicking "Add" repeatedly reuses the same `formCapsule` state object in CapsuleGroup.tsx). CapsuleGroup.tsx
 * relies on that per-entry identity to know which row is being edited/removed via `===` and lodash's `_.without`.
 */
const sanitizeCapsule = (capsule: CapsuleGroupCapsule): CapsuleGroupCapsule => ({
  id: capsule.id,
  startTime: capsule.startTime,
  endTime: capsule.endTime,
  properties: capsule.properties,
  propertiesSource: capsule.propertiesSource,
});

const initialState: CapsuleGroupState = Object.freeze({
  capsules: [],
  isLoading: false,
});

/** Excludes `isLoading`, which is transient UI state that should not be persisted with the workstep. */
export type CapsuleGroupDehydratedState = Pick<CapsuleGroupState, 'capsules'>;

export const dehydrateCapsuleGroupState = (state: CapsuleGroupState): CapsuleGroupDehydratedState => ({
  capsules: state.capsules,
});

const capsuleGroupActionsFactory: ActionsFactory<CapsuleGroupState, CapsuleGroupActions> = (set, _get, resetState) => ({
  setLoading: (isLoading) =>
    set((draft) => {
      draft.isLoading = isLoading;
    }),

  setCapsules: (capsules) =>
    set((draft) => {
      draft.capsules = capsules.map(sanitizeCapsule);
      draft.isLoading = false;
    }),

  addCapsule: (capsule) =>
    set((draft) => {
      draft.capsules.push(sanitizeCapsule(capsule));
    }),

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'CapsuleGroup';

const [CapsuleGroupStoreProvider, useCapsuleGroupStoreInstance, useCapsuleGroupSelector] = createStore(
  STORE_NAME,
  initialState,
  capsuleGroupActionsFactory,
);

export { CapsuleGroupStoreProvider };

export const useCapsuleGroupCapsules = () => useCapsuleGroupSelector((state) => state.capsules);
export const useCapsuleGroupIsLoading = () => useCapsuleGroupSelector((state) => state.isLoading);
/** True if the capsule group has at least one capsule; used to drive form validation. */
export const useCapsuleGroupHasAtLeastOneCapsule = () => useCapsuleGroupSelector((state) => state.capsules.length > 0);

/** Returns the CapsuleGroup store actions for updating state. */
export const useCapsuleGroupActions = () => useCapsuleGroupStoreInstance().actions;

/**
 * @deprecated For legacy singleton bridge only. Non-React consumers (action files) use the singleton created from
 * this in core.stores.ts. DO NOT use in new code.
 */
export function legacyCreateCapsuleGroupStore() {
  return createStoreInstance<CapsuleGroupState, CapsuleGroupActions>(initialState, capsuleGroupActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistCapsuleGroupStore = () => {
  const register = useRegisterPersistedStore();
  const store = useCapsuleGroupStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqCapsuleGroupStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateCapsuleGroupState,
    });
  }, [register, store]);
};
